import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card,
  Select,
  Button,
  Space,
  Typography,
  Row,
  Col,
  Alert,
  Divider,
  Spin,
  Progress,
  Tree,
  Tag,
  Empty,
  Input,
  App,
  Tabs,
  List,
  Statistic,
  Badge,
  Table,
  Popconfirm,
  Tooltip,
  Pagination,
  Modal,
} from 'antd';
import {
  DatabaseOutlined,
  PlayCircleOutlined,
  SearchOutlined,
  TableOutlined,
  LinkOutlined,
  ReloadOutlined,
  BugOutlined,
  RocketOutlined,
  DeleteOutlined,
  EyeOutlined,
  FileTextOutlined,
  NodeIndexOutlined,
} from '@ant-design/icons';
import { useProjects } from '../hooks/useProjects';
import { useDataSources } from '../hooks/useDataSources';
import { useAppSelector } from '../store/hooks';
import api from '../lib/api';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { Search } = Input;

interface DataSource {
  id: string;
  name: string;
  description?: string;
  connectorType: string;
  config: any;
}

interface SchemaExtractionResult {
  dataSourceId: string;
  dataSourceName: string;
  success: boolean;
  schema?: any;
  error?: string;
}


interface TreeNode {
  key: string;
  title: React.ReactNode;
  children?: TreeNode[];
  icon?: React.ReactNode;
  isLeaf?: boolean;
}

const QueryGeneratorPage: React.FC = () => {
  const { message } = App.useApp();
  
  // Tab state
  const [activeTab, setActiveTab] = useState<string>('schema-extraction');
  
  // Schema extraction state
  const [selectedDataSources, setSelectedDataSources] = useState<string[]>([]);
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractionResults, setExtractionResults] = useState<SchemaExtractionResult[]>([]);
  const [schemaTreeData, setSchemaTreeData] = useState<TreeNode[]>([]);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
  const [filteredTreeData, setFilteredTreeData] = useState<TreeNode[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingSavedSchemas, setIsLoadingSavedSchemas] = useState(false);
  const [savedSchemaResults, setSavedSchemaResults] = useState<SchemaExtractionResult[]>([]);
  const [isUpsertingToVector, setIsUpsertingToVector] = useState(false);
  
  // Vector testing state
  const [vectorSearchQuery, setVectorSearchQuery] = useState<string>('');
  const [vectorSearchResults, setVectorSearchResults] = useState<any[]>([]);
  const [isSearchingVectors, setIsSearchingVectors] = useState(false);
  const [vectorStats, setVectorStats] = useState<any>(null);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [availableDataSources, setAvailableDataSources] = useState<any[]>([]);
  const [selectedVectorDataSource, setSelectedVectorDataSource] = useState<string>('');
  
  // Vector list state
  const [vectors, setVectors] = useState<any[]>([]);
  const [isLoadingVectors, setIsLoadingVectors] = useState(false);
  const [vectorPage, setVectorPage] = useState(1);
  const [vectorPageSize, setVectorPageSize] = useState(20);
  const [vectorTotal, setVectorTotal] = useState(0);
  const [vectorFilter, setVectorFilter] = useState<{
    connector?: string;
    type?: string;
    source?: string;
  }>({});
  const [sourceFilterInput, setSourceFilterInput] = useState<string>('');
  const [selectedVector, setSelectedVector] = useState<any>(null);
  const [isVectorDetailModalVisible, setIsVectorDetailModalVisible] = useState(false);
  const [isLoadingVectorDetail, setIsLoadingVectorDetail] = useState(false);

  // Smart Query states
  const [smartQuery, setSmartQuery] = useState<string>('');
  const [smartQueryResult, setSmartQueryResult] = useState<any>(null);
  const [isProcessingSmartQuery, setIsProcessingSmartQuery] = useState(false);
  const [selectedSmartQueryDataSource, setSelectedSmartQueryDataSource] = useState<string>('');

  const { currentProject } = useAppSelector((state) => state.project);
  const { data: projects, isLoading: projectsLoading } = useProjects();
  const { data: dataSources, isLoading: dataSourcesLoading, refetch: refetchDataSources } = useDataSources(currentProject?.id || '');

  // Database connector types
  const databaseConnectorTypes = ['mssql', 'postgresql', 'mysql', 'oracle', 'sqlite'];

  // Filter data sources to show only database types
  const databaseDataSources = dataSources?.filter((ds: DataSource) => 
    databaseConnectorTypes.includes(ds.connectorType.toLowerCase())
  ) || [];

  useEffect(() => {
    if (currentProject?.id) {
      refetchDataSources();
    }
  }, [currentProject?.id, refetchDataSources]);
  
  // Project is now managed through global context only
  
  // Smart Query processing function
  const processSmartQuery = useCallback(async () => {
    if (!smartQuery.trim() || !currentProject?.id || !selectedSmartQueryDataSource) {
      message.error('Please enter a query and select a data source');
      return;
    }

    setIsProcessingSmartQuery(true);
    setSmartQueryResult(null);

    try {
      const response = await api.post('/query-generator/smart-query', {
        query: smartQuery,
        projectId: currentProject.id,
        dataSourceId: selectedSmartQueryDataSource,
      });

      setSmartQueryResult(response.data);
      if (response.data.success) {
        message.success('Smart query processed successfully!');
      } else {
        message.error(`Smart query failed: ${response.data.error}`);
      }
    } catch (error: any) {
      console.error('Smart query error:', error);
      message.error(`Smart query failed: ${error.response?.data?.message || error.message}`);
    } finally {
      setIsProcessingSmartQuery(false);
    }
  }, [smartQuery, currentProject?.id, selectedSmartQueryDataSource]);
  
  // Vector list functions
  const loadVectors = useCallback(async () => {
    if (!currentProject?.id) return;
    
    setIsLoadingVectors(true);
    
    try {
      const params = new URLSearchParams();
      if (vectorFilter.connector) params.append('connector', vectorFilter.connector);
      if (vectorFilter.type) params.append('type', vectorFilter.type);
      if (vectorFilter.source) params.append('source', vectorFilter.source);
      
      // Add pagination parameters
      params.append('page', vectorPage.toString());
      params.append('limit', vectorPageSize.toString());
      
      const response = await api.get(`/projects/${currentProject.id}/vectors?${params}`);
      
      // Server-side pagination
      const { vectors, total } = response.data || { vectors: [], total: 0 };
      setVectors(vectors);
      setVectorTotal(total);
    } catch (error: any) {
      console.error('Failed to load vectors:', error);
      message.error('Failed to load vectors');
    } finally {
      setIsLoadingVectors(false);
    }
  }, [currentProject?.id, vectorFilter, vectorPage, vectorPageSize]);
  
  const deleteVector = useCallback(async (vectorId: string) => {
    if (!currentProject?.id) return;
    
    try {
      await api.delete(`/projects/${currentProject.id}/vectors/${vectorId}`);
      message.success('Vector deleted successfully');
      loadVectors(); // Reload the list
    } catch (error: any) {
      console.error('Failed to delete vector:', error);
      message.error(`Failed to delete vector: ${error.response?.data?.message || error.message}`);
    }
  }, [currentProject?.id, loadVectors, message]);
  
  const showVectorDetail = async (vector: any) => {
    setSelectedVector(vector);
    setIsVectorDetailModalVisible(true);
    
    // Fetch full vector data if not already available
    if (!vector.content || !vector.vectors) {
      setIsLoadingVectorDetail(true);
      try {
        const response = await api.get(`/projects/${currentProject!.id}/vectors/${vector.documentId}`);
        setSelectedVector(response.data);
      } catch (error) {
        console.error('Failed to fetch full vector data:', error);
        message.error('Failed to load vector details');
      } finally {
        setIsLoadingVectorDetail(false);
      }
    }
  };
  
  // Debounce source filter input
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setVectorFilter(prev => ({ ...prev, source: sourceFilterInput || undefined }));
    }, 500); // 500ms debounce delay

    return () => clearTimeout(timeoutId);
  }, [sourceFilterInput]);

  // Load vectors when project changes or active tab is vectors
  useEffect(() => {
    if (activeTab === 'vectors' && currentProject?.id) {
      loadVectors();
    }
  }, [activeTab, currentProject?.id, loadVectors]);

  // Auto-load saved schemas when data sources are selected
  useEffect(() => {
    if (currentProject?.id && selectedDataSources.length > 0) {
      loadSavedSchemasForSelectedDataSources();
    } else {
      setSavedSchemaResults([]);
      setSchemaTreeData([]);
    }
  }, [currentProject?.id, selectedDataSources]);

  // Debounce search input
  useEffect(() => {
    if (searchValue !== debouncedSearchValue) {
      setIsSearching(true);
    }
    
    const timeoutId = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
      setIsSearching(false);
    }, 300); // 300ms debounce delay

    return () => clearTimeout(timeoutId);
  }, [searchValue, debouncedSearchValue]);

  // Memoized text extraction cache - clear when schema data changes
  const textExtractionCache = useMemo(() => new Map<React.ReactNode, string>(), [schemaTreeData]);

  // Optimized helper function to extract text from JSX elements for search
  const extractTextFromJSX = useCallback((element: React.ReactNode): string => {
    if (textExtractionCache.has(element)) {
      return textExtractionCache.get(element)!;
    }

    let result = '';
    if (typeof element === 'string') {
      result = element;
    } else if (typeof element === 'number') {
      result = element.toString();
    } else if (React.isValidElement(element)) {
      if (element.props.children) {
        result = React.Children.toArray(element.props.children)
          .map(child => {
            if (typeof child === 'string') return child;
            if (typeof child === 'number') return child.toString();
            return extractTextFromJSX(child);
          })
          .join(' ');
      }
    }

    textExtractionCache.set(element, result);
    return result;
  }, [textExtractionCache]);

  const filterTreeData = useCallback((data: TreeNode[], searchValue: string): TreeNode[] => {
    const search = searchValue.toLowerCase();
    
    const filterNode = (node: TreeNode): TreeNode | null => {
      // Convert title to string for searching
      const titleString = typeof node.title === 'string' ? node.title : 
        React.isValidElement(node.title) ? extractTextFromJSX(node.title) : '';
      
      const matches = titleString.toLowerCase().includes(search);
      
      if (node.children) {
        const filteredChildren = node.children.map(filterNode).filter(Boolean) as TreeNode[];
        if (filteredChildren.length > 0 || matches) {
          return { ...node, children: filteredChildren };
        }
      }
      
      return matches ? node : null;
    };
    
    return data.map(filterNode).filter(Boolean) as TreeNode[];
  }, [extractTextFromJSX]);

  const getAllExpandedKeys = useCallback((data: TreeNode[]): string[] => {
    const keys: string[] = [];
    const traverse = (nodes: TreeNode[]) => {
      for (const node of nodes) {
        if (node.children && node.children.length > 0) {
          keys.push(node.key);
          traverse(node.children);
        }
      }
    };
    traverse(data);
    return keys;
  }, []);

  // Memoized search results
  const searchResults = useMemo(() => {
    if (schemaTreeData.length > 0) {
      if (debouncedSearchValue) {
        const filtered = filterTreeData(schemaTreeData, debouncedSearchValue);
        // Auto-expand filtered results
        const expandedKeys = getAllExpandedKeys(filtered);
        setExpandedKeys(expandedKeys);
        return filtered;
      } else {
        return schemaTreeData;
      }
    }
    return [];
  }, [debouncedSearchValue, schemaTreeData, filterTreeData, getAllExpandedKeys]);

  useEffect(() => {
    setFilteredTreeData(searchResults);
  }, [searchResults]);

  const handleExtractSchemas = async () => {
    if (!currentProject?.id || selectedDataSources.length === 0) {
      message.warning('Please select a project and at least one data source');
      return;
    }

    setIsExtracting(true);
    setExtractionResults([]);
    setSchemaTreeData([]);

    try {
      // Call batch extraction API with API key authentication
      const apiKey = 'ak_6733133373d7f0cbc00aec82742fdb70d0ca33b2'; // From development prompt
      const response = await api.post(`/projects/${currentProject.id}/query/extract-all-schemas`, {
        includeSampleData: true,
        includeRelationships: true,
        storeInVector: true, // Enable vector storage for intelligent schema search
      }, {
        headers: {
          'X-API-Key': apiKey
        }
      });

      if (response.data.success) {
        setExtractionResults(response.data.results);
        
        // Convert results to tree structure
        const treeData = convertResultsToTreeData(response.data.results);
        setSchemaTreeData(treeData);
        
        message.success(`Schema extraction completed! ${response.data.results.filter((r: SchemaExtractionResult) => r.success).length} data sources processed successfully.`);
      } else {
        message.error('Schema extraction failed');
      }
    } catch (error: any) {
      console.error('Schema extraction error:', error);
      message.error(`Extraction failed: ${error.response?.data?.message || error.message}`);
    } finally {
      setIsExtracting(false);
    }
  };

  const convertResultsToTreeData = (results: SchemaExtractionResult[]): TreeNode[] => {
    return results
      .filter(result => result.success && result.schema)
      .map(result => {
        const schema = result.schema;
        
        // Check if this is a newly extracted schema (for color coding)
        const isNewExtraction = true; // We can enhance this later to check against saved schemas
        
        const databaseNode: TreeNode = {
          key: `db-${result.dataSourceId}`,
          title: (
            <Space>
              <DatabaseOutlined style={{ color: isNewExtraction ? '#52c41a' : '#1890ff' }} />
              <Text strong style={{ color: isNewExtraction ? '#52c41a' : 'inherit' }}>
                {result.dataSourceName}
              </Text>
              <Tag color="blue">{schema.database}</Tag>
              <Tag color={isNewExtraction ? "green" : "geekblue"}>{schema.tables.length} tables</Tag>
              {isNewExtraction && <Tag color="orange">NEW</Tag>}
            </Space>
          ),
          children: schema.tables.map((table: any, tableIndex: number) => {
            // Simulate some tables being new (every 3rd table for demo)
            const isNewTable = tableIndex % 3 === 0;
            const isModifiedTable = tableIndex % 4 === 0 && !isNewTable;
            
            return {
              key: `table-${result.dataSourceId}-${table.name}`,
              title: (
                <Space>
                  <TableOutlined style={{ color: isNewTable ? '#52c41a' : isModifiedTable ? '#fa8c16' : '#52c41a' }} />
                  <Text style={{ color: isNewTable ? '#52c41a' : isModifiedTable ? '#fa8c16' : 'inherit' }}>
                    {table.name}
                  </Text>
                  <Tag color={isNewTable ? "green" : isModifiedTable ? "orange" : "orange"}>
                    {table.columns.length} columns
                  </Tag>
                  {table.foreignKeys && table.foreignKeys.length > 0 && (
                    <Tag color="purple">{table.foreignKeys.length} FK</Tag>
                  )}
                  {isNewTable && <Tag color="green">NEW</Tag>}
                  {isModifiedTable && <Tag color="orange">MODIFIED</Tag>}
                </Space>
              ),
              children: [
                // Columns section
                {
                  key: `columns-${result.dataSourceId}-${table.name}`,
                  title: (
                    <Space>
                      <Text type="secondary">📋 Columns ({table.columns.length})</Text>
                    </Space>
                  ),
                  children: table.columns.map((column: any, colIndex: number) => {
                    // Simulate some columns being new/modified
                    const isNewColumn = colIndex % 5 === 0;
                    const isModifiedColumn = colIndex % 7 === 0 && !isNewColumn;
                    
                    return {
                      key: `column-${result.dataSourceId}-${table.name}-${column.name}`,
                      title: (
                        <Space>
                          <Text style={{ color: isNewColumn ? '#52c41a' : isModifiedColumn ? '#fa8c16' : 'inherit' }}>
                            {column.name}
                          </Text>
                          <Tag color="geekblue">{column.dataType}</Tag>
                          {column.isPrimaryKey && <Tag color="red">PK</Tag>}
                          {!column.nullable && <Tag color="orange">NOT NULL</Tag>}
                          {column.defaultValue && <Tag color="cyan">DEFAULT</Tag>}
                          {isNewColumn && <Tag color="green">NEW</Tag>}
                          {isModifiedColumn && <Tag color="orange">MODIFIED</Tag>}
                        </Space>
                      ),
                      isLeaf: true,
                    };
                  }),
                },
                // Foreign keys section (if any)
                ...(table.foreignKeys && table.foreignKeys.length > 0 ? [{
                  key: `fks-${result.dataSourceId}-${table.name}`,
                  title: (
                    <Space>
                      <Text type="secondary">🔗 Foreign Keys ({table.foreignKeys.length})</Text>
                    </Space>
                  ),
                  children: table.foreignKeys.map((fk: any, index: number) => ({
                    key: `fk-${result.dataSourceId}-${table.name}-${index}`,
                    title: (
                      <Space>
                        <LinkOutlined style={{ color: '#722ed1' }} />
                        <Text>{fk.columnName} → {fk.referencedTable}.{fk.referencedColumn}</Text>
                      </Space>
                    ),
                    isLeaf: true,
                  })),
                }] : []),
                // Sample data section (if any)
                ...(table.sampleData && table.sampleData.length > 0 ? [{
                  key: `samples-${result.dataSourceId}-${table.name}`,
                  title: (
                    <Space>
                      <Text type="secondary">📊 Sample Data ({table.sampleData.length} rows)</Text>
                    </Space>
                  ),
                  isLeaf: true,
                }] : []),
              ],
            };
          }),
        };
        return databaseNode;
      });
  };

  const convertSavedSchemasToTreeData = (results: SchemaExtractionResult[]): TreeNode[] => {
    return results
      .filter(result => result.success && result.schema)
      .map(result => {
        const schema = result.schema;
        const metadata = (result as any).metadata;
        
        const lastSyncDate = metadata?.lastSyncAt ? new Date(metadata.lastSyncAt).toLocaleDateString() : 'Unknown';
        
        const databaseNode: TreeNode = {
          key: `saved-db-${result.dataSourceId}`,
          title: (
            <Space>
              <DatabaseOutlined style={{ color: '#722ed1' }} />
              <Text strong style={{ color: '#722ed1' }}>
                {result.dataSourceName}
              </Text>
              <Tag color="purple">{schema.database}</Tag>
              <Tag color="blue">{schema.tables.length} tables</Tag>
              <Tag color="purple">SAVED</Tag>
              <Tag color="default" style={{ fontSize: '11px' }}>v{metadata?.syncVersion}</Tag>
            </Space>
          ),
          children: schema.tables.map((table: any) => {
            return {
              key: `saved-table-${result.dataSourceId}-${table.name}`,
              title: (
                <Space>
                  <TableOutlined style={{ color: '#722ed1' }} />
                  <Text style={{ color: '#722ed1' }}>
                    {table.name}
                  </Text>
                  <Tag color="blue">{table.columns.length} columns</Tag>
                  {table.foreignKeys && table.foreignKeys.length > 0 && (
                    <Tag color="purple">{table.foreignKeys.length} FK</Tag>
                  )}
                  <Tag color="purple" style={{ fontSize: '10px' }}>SAVED</Tag>
                </Space>
              ),
              children: [
                // Columns section
                {
                  key: `saved-columns-${result.dataSourceId}-${table.name}`,
                  title: (
                    <Space>
                      <Text type="secondary">📋 Columns ({table.columns.length}) - Last sync: {lastSyncDate}</Text>
                    </Space>
                  ),
                  children: table.columns.map((column: any) => {
                    return {
                      key: `saved-column-${result.dataSourceId}-${table.name}-${column.name}`,
                      title: (
                        <Space>
                          <Text style={{ color: '#722ed1' }}>
                            {column.name}
                          </Text>
                          <Tag color="geekblue">{column.dataType}</Tag>
                          {column.isPrimaryKey && <Tag color="red">PK</Tag>}
                          {!column.nullable && <Tag color="orange">NOT NULL</Tag>}
                          {column.defaultValue && <Tag color="cyan">DEFAULT</Tag>}
                        </Space>
                      ),
                      isLeaf: true,
                    };
                  }),
                },
                // Foreign keys section (if any)
                ...(table.foreignKeys && table.foreignKeys.length > 0 ? [{
                  key: `saved-fks-${result.dataSourceId}-${table.name}`,
                  title: (
                    <Space>
                      <Text type="secondary">🔗 Foreign Keys ({table.foreignKeys.length})</Text>
                    </Space>
                  ),
                  children: table.foreignKeys.map((fk: any, index: number) => ({
                    key: `saved-fk-${result.dataSourceId}-${table.name}-${index}`,
                    title: (
                      <Space>
                        <LinkOutlined style={{ color: '#722ed1' }} />
                        <Text>{fk.columnName} → {fk.referencedTable}.{fk.referencedColumn}</Text>
                      </Space>
                    ),
                    isLeaf: true,
                  })),
                }] : []),
                // Sample data section (if any)
                ...(table.sampleData && table.sampleData.length > 0 ? [{
                  key: `saved-samples-${result.dataSourceId}-${table.name}`,
                  title: (
                    <Space>
                      <Text type="secondary">📊 Sample Data ({table.sampleData.length} rows)</Text>
                    </Space>
                  ),
                  isLeaf: true,
                }] : []),
              ],
            };
          }),
        };
        return databaseNode;
      });
  };

  const getSuccessCount = () => extractionResults.filter(r => r.success).length;
  const getTotalCount = () => extractionResults.length;
  const getProgressPercentage = () => {
    if (getTotalCount() === 0) return 0;
    return Math.round((getSuccessCount() / getTotalCount()) * 100);
  };

  const handleSaveSchemas = async () => {
    if (!currentProject?.id || extractionResults.length === 0) {
      message.warning('No schemas to save');
      return;
    }

    setIsSaving(true);
    
    try {
      const apiKey = 'ak_6733133373d7f0cbc00aec82742fdb70d0ca33b2';
      const savePromises = extractionResults
        .filter(result => result.success && result.schema)
        .map(async (result) => {
          const response = await api.post(`/projects/${currentProject.id}/query/save-schema`, {
            dataSourceId: result.dataSourceId,
            schema: result.schema
          }, {
            headers: { 'X-API-Key': apiKey }
          });
          return { ...response.data, dataSourceName: result.dataSourceName };
        });

      const saveResults = await Promise.all(savePromises);
      const successCount = saveResults.filter(r => r.success).length;
      
      if (successCount === saveResults.length) {
        message.success(`Successfully saved ${successCount} schemas to MongoDB!`);
      } else {
        message.warning(`Saved ${successCount}/${saveResults.length} schemas. Some failed to save.`);
      }

      // Show save results
      saveResults.forEach(result => {
        console.log('💾 Save Result:', result);
      });

    } catch (error: any) {
      console.error('Save error:', error);
      message.error(`Failed to save schemas: ${error.response?.data?.message || error.message}`);
    } finally {
      setIsSaving(false);
    }
  };


  const loadSavedSchemasForSelectedDataSources = async () => {
    if (!currentProject?.id || selectedDataSources.length === 0) return;

    setIsLoadingSavedSchemas(true);
    console.log('🔄 Loading saved schemas for selected data sources...');

    try {
      const apiKey = 'ak_6733133373d7f0cbc00aec82742fdb70d0ca33b2';
      const response = await api.post(`/projects/${currentProject.id}/query/load-saved-schemas`, {
        dataSourceIds: selectedDataSources
      }, {
        headers: { 'X-API-Key': apiKey }
      });

      if (response.data.success) {
        const savedResults: SchemaExtractionResult[] = [];
        
        // Convert saved schema results to match extraction result format
        response.data.results.forEach((result: any) => {
          if (result.success) {
            const dataSource = databaseDataSources.find(ds => ds.id === result.dataSourceId);
            savedResults.push({
              dataSourceId: result.dataSourceId,
              dataSourceName: dataSource?.name || 'Unknown',
              success: true,
              schema: result.schema,
              ...(result as any).metadata && { metadata: (result as any).metadata }
            });
          }
        });

        if (savedResults.length > 0) {
          console.log(`✅ Loaded ${savedResults.length} saved schemas`);
          setSavedSchemaResults(savedResults);
          
          // Convert to tree data and display
          const treeData = convertSavedSchemasToTreeData(savedResults);
          setSchemaTreeData(treeData);
          
          message.success(`Loaded ${savedResults.length} saved schema(s) from MongoDB`);
        } else {
          console.log('ℹ️ No saved schemas found for selected data sources');
          setSavedSchemaResults([]);
          setSchemaTreeData([]);
        }
      }
    } catch (error: any) {
      console.error('Failed to load saved schemas:', error);
      // Don't show error message for auto-loading, just log it
      setSavedSchemaResults([]);
      setSchemaTreeData([]);
    } finally {
      setIsLoadingSavedSchemas(false);
    }
  };

  const handleUpsertSchemasToVector = async () => {
    if (!currentProject?.id || savedSchemaResults.length === 0) {
      message.warning('No saved schemas to upsert to vector database');
      return;
    }

    setIsUpsertingToVector(true);
    console.log('🧠 Upserting schemas to vector database...');

    try {
      const apiKey = 'ak_6733133373d7f0cbc00aec82742fdb70d0ca33b2';
      const upsertPromises = savedSchemaResults.map(async (result) => {
        const response = await api.post(`/projects/${currentProject.id}/query/upsert-schema-vectors`, {
          dataSourceId: result.dataSourceId
        }, {
          headers: { 'X-API-Key': apiKey }
        });
        return { ...response.data, dataSourceName: result.dataSourceName };
      });

      const upsertResults = await Promise.all(upsertPromises);
      const successCount = upsertResults.filter(r => r.success).length;
      
      if (successCount === upsertResults.length) {
        message.success(`Successfully upserted ${successCount} schemas to vector database!`);
        console.log(`✅ All ${successCount} schemas upserted to vector database`);
      } else {
        message.warning(`Upserted ${successCount}/${upsertResults.length} schemas. Some failed.`);
        console.log(`⚠️ Mixed results: ${successCount}/${upsertResults.length} successful`);
      }

      // Show upsert results
      upsertResults.forEach((result, index) => {
        console.log(`   ${index + 1}. ${result.dataSourceName}: ${result.success ? '✅' : '❌'} ${result.message}`);
      });

    } catch (error: any) {
      console.error('Upsert error:', error);
      message.error(`Failed to upsert schemas: ${error.response?.data?.message || error.message}`);
    } finally {
      setIsUpsertingToVector(false);
    }
  };


  // Vector testing functions
  const loadProjectDataSources = async () => {
    if (!currentProject?.id) return;
    
    try {
      const apiKey = 'ak_6733133373d7f0cbc00aec82742fdb70d0ca33b2';
      const response = await api.get(`/query-generator/project-datasources/${currentProject.id}`, {
        headers: { 'X-API-Key': apiKey }
      });
      
      if (response.data.success) {
        setAvailableDataSources(response.data.dataSources);
        // Auto-select first MSSQL data source with vectors if available
        const mssqlWithVectors = response.data.dataSources.find((ds: any) => 
          ds.connectorType === 'mssql' && ds.hasVectors
        );
        if (mssqlWithVectors && !selectedVectorDataSource) {
          setSelectedVectorDataSource(mssqlWithVectors.id);
        }
      }
    } catch (error: any) {
      console.error('Failed to load data sources for vector testing:', error);
    }
  };
  
  const searchFieldVectors = async () => {
    if (!currentProject?.id || !selectedVectorDataSource || !vectorSearchQuery.trim()) {
      message.warning('Please select a project, data source, and enter a search query');
      return;
    }
    
    setIsSearchingVectors(true);
    
    try {
      const apiKey = 'ak_6733133373d7f0cbc00aec82742fdb70d0ca33b2';
      const response = await api.post('/query-generator/test-vector-search', {
        projectId: currentProject.id,
        dataSourceId: selectedVectorDataSource,
        searchQuery: vectorSearchQuery,
        limit: 10,
        includeMetadata: true
      }, {
        headers: { 'X-API-Key': apiKey }
      });
      
      if (response.data.success) {
        setVectorSearchResults(response.data.results || []);
        message.success(`Found ${response.data.totalResults} matching fields in ${response.data.processingTime}ms`);
      } else {
        setVectorSearchResults([]);
        message.info(response.data.message || 'No results found');
      }
    } catch (error: any) {
      console.error('Vector search error:', error);
      message.error(`Search failed: ${error.response?.data?.message || error.message}`);
      setVectorSearchResults([]);
    } finally {
      setIsSearchingVectors(false);
    }
  };
  
  const loadVectorStats = async () => {
    if (!currentProject?.id) return;
    
    setIsLoadingStats(true);
    
    try {
      const apiKey = 'ak_6733133373d7f0cbc00aec82742fdb70d0ca33b2';
      const response = await api.post('/query-generator/vector-stats', {
        projectId: currentProject.id,
        dataSourceId: selectedVectorDataSource
      }, {
        headers: { 'X-API-Key': apiKey }
      });
      
      if (response.data.success) {
        setVectorStats(response.data.stats);
      }
    } catch (error: any) {
      console.error('Failed to load vector stats:', error);
    } finally {
      setIsLoadingStats(false);
    }
  };
  
  // Load data sources when project changes for vector testing
  useEffect(() => {
    if (currentProject?.id && activeTab === 'test-vector') {
      loadProjectDataSources();
      loadVectorStats();
    }
  }, [currentProject?.id, activeTab]);
  
  // Load vector stats when data source changes
  useEffect(() => {
    if (currentProject?.id && selectedVectorDataSource && activeTab === 'test-vector') {
      loadVectorStats();
    }
  }, [currentProject?.id, selectedVectorDataSource]);
  
  // Tab items
  const tabItems = [
    {
      key: 'schema-extraction',
      label: (
        <Space>
          <DatabaseOutlined />
          Schema Extraction
        </Space>
      ),
    },
    {
      key: 'test-vector',
      label: (
        <Space>
          <SearchOutlined />
          Test Vector
          {vectorStats && vectorStats.schemaFieldVectors > 0 && (
            <Badge count={vectorStats.schemaFieldVectors} size="small" />
          )}
        </Space>
      ),
    },
    {
      key: 'smart-query',
      label: (
        <Space>
          <RocketOutlined />
          Smart Query
        </Space>
      ),
    },
    {
      key: 'vectors',
      label: (
        <Space>
          <NodeIndexOutlined />
          Vectors
          {vectorTotal > 0 && (
            <Badge count={vectorTotal} size="small" />
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '12px' }}>
          <DatabaseOutlined style={{ color: '#1890ff' }} />
          Query Generator
        </Title>
        <Paragraph type="secondary" style={{ margin: '8px 0 0 0', fontSize: '16px' }}>
          Extract database schemas and test field-level vector search capabilities
        </Paragraph>
      </div>
      
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        size="large"
        style={{ marginBottom: '24px' }}
      />

      {activeTab === 'schema-extraction' && (
        <Row gutter={[24, 24]}>
        {/* Configuration Panel */}
        <Col xs={24} lg={8}>
          <Card title="Configuration" style={{ height: '100%' }}>
            <Space direction="vertical" style={{ width: '100%' }} size="large">
              {currentProject && (
                <Alert
                  type="info"
                  message={`Working with project: ${currentProject.name}`}
                  style={{ marginBottom: '16px' }}
                />
              )}
              
              {!currentProject && (
                <Alert
                  type="warning"
                  message="Please select a project from the header to continue"
                  style={{ marginBottom: '16px' }}
                />
              )}

              {currentProject && (
                <div>
                  <Text strong style={{ display: 'block', marginBottom: '8px' }}>
                    Database Sources ({databaseDataSources.length})
                  </Text>
                  <Select
                    mode="multiple"
                    style={{ width: '100%' }}
                    placeholder="Select database sources"
                    loading={dataSourcesLoading}
                    value={selectedDataSources}
                    onChange={setSelectedDataSources}
                    maxTagCount="responsive"
                  >
                    {databaseDataSources.map((ds: DataSource) => (
                      <Option key={ds.id} value={ds.id}>
                        <Space>
                          <DatabaseOutlined />
                          {ds.name}
                          <Tag color="blue">
                            {ds.connectorType.toUpperCase()}
                          </Tag>
                        </Space>
                      </Option>
                    ))}
                  </Select>
                  {databaseDataSources.length === 0 && (
                    <Alert
                      type="info"
                      message="No database sources found"
                      description="Add database connectors (MSSQL, PostgreSQL, MySQL, etc.) to your project first."
                      style={{ marginTop: '12px' }}
                      showIcon
                    />
                  )}
                </div>
              )}

              <Divider />

              <Button
                type="primary"
                size="large"
                icon={<PlayCircleOutlined />}
                onClick={handleExtractSchemas}
                loading={isExtracting}
                disabled={!currentProject?.id || selectedDataSources.length === 0}
                style={{ width: '100%' }}
              >
                {isExtracting ? 'Extracting Schemas...' : 'Extract Schemas'}
              </Button>

              {extractionResults.length > 0 && getSuccessCount() > 0 && (
                <Button
                  type="default"
                  size="large"
                  icon={<DatabaseOutlined />}
                  onClick={handleSaveSchemas}
                  loading={isSaving}
                  style={{ width: '100%', marginTop: '12px' }}
                >
                  {isSaving ? 'Saving to MongoDB...' : `Save ${getSuccessCount()} Schemas to MongoDB`}
                </Button>
              )}


              {isLoadingSavedSchemas && (
                <div style={{ marginTop: '12px', textAlign: 'center' }}>
                  <Spin size="small" />
                  <Text type="secondary" style={{ marginLeft: '8px', fontSize: '12px' }}>
                    Loading saved schemas...
                  </Text>
                </div>
              )}

              {isExtracting && (
                <div>
                  <Text strong>Extraction Progress</Text>
                  <Progress
                    percent={getProgressPercentage()}
                    status={isExtracting ? 'active' : 'success'}
                    format={() => `${getSuccessCount()}/${getTotalCount()}`}
                  />
                  <Spin size="small" /> <Text type="secondary">Processing data sources...</Text>
                </div>
              )}

              {extractionResults.length > 0 && !isExtracting && (
                <div>
                  <Text strong>Extraction Results</Text>
                  <div style={{ marginTop: '8px' }}>
                    {extractionResults.map((result, index) => (
                      <div key={index} style={{ marginBottom: '4px' }}>
                        <Space>
                          {result.success ? (
                            <Tag color="success">✓</Tag>
                          ) : (
                            <Tag color="error">✗</Tag>
                          )}
                          <Text style={{ fontSize: '12px' }}>
                            {result.dataSourceName}
                          </Text>
                        </Space>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Space>
          </Card>
        </Col>

        {/* Schema Visualization Panel */}
        <Col xs={24} lg={16}>
          <Card 
            title={
              <Space>
                <Text strong>Schema Tree View</Text>
                {schemaTreeData.length > 0 && (
                  <>
                    <Tag color="blue">{schemaTreeData.length} databases</Tag>
                    {savedSchemaResults.length > 0 && extractionResults.length === 0 && (
                      <Tag color="purple">SAVED SCHEMAS</Tag>
                    )}
                    {extractionResults.length > 0 && (
                      <Tag color="green">NEWLY EXTRACTED</Tag>
                    )}
                  </>
                )}
              </Space>
            }
            extra={
              schemaTreeData.length > 0 && (
                <Space>
                  {savedSchemaResults.length > 0 && (
                    <Button
                      icon={<RocketOutlined />}
                      onClick={handleUpsertSchemasToVector}
                      loading={isUpsertingToVector}
                      size="large"
                      type="primary"
                      style={{ 
                        backgroundColor: '#52c41a',
                        borderColor: '#52c41a',
                        borderRadius: '8px',
                        fontWeight: 'bold',
                        height: '40px',
                        paddingLeft: '24px',
                        paddingRight: '24px'
                      }}
                    >
                      {isUpsertingToVector ? 'Running...' : 'Run Vector Upsert'}
                    </Button>
                  )}
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={() => {
                      setSearchValue('');
                      setExpandedKeys([]);
                    }}
                    size="small"
                  >
                    Reset View
                  </Button>
                </Space>
              )
            }
            style={{ height: '600px' }}
            styles={{ body: { height: 'calc(100% - 57px)', overflow: 'hidden' } }}
          >
            {schemaTreeData.length > 0 ? (
              <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <div style={{ marginBottom: '16px' }}>
                  <Search
                    placeholder="Search tables, columns, relationships, or data types..."
                    value={searchValue}
                    onChange={(e) => {
                      console.log('Search input changed:', e.target.value);
                      setSearchValue(e.target.value);
                    }}
                    style={{ width: '100%' }}
                    enterButton={isSearching ? <Spin size="small" /> : <SearchOutlined />}
                    allowClear
                    size="large"
                    disabled={false}
                  />
                  {searchValue && (
                    <div style={{ marginTop: '8px', display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {isSearching ? 'Searching...' : 'Search tips:'}
                      </Text>
                      {!isSearching && (
                        <>
                          <Tag color="blue" style={{ fontSize: '10px' }}>Table names</Tag>
                          <Tag color="green" style={{ fontSize: '10px' }}>Column names</Tag>
                          <Tag color="purple" style={{ fontSize: '10px' }}>Data types</Tag>
                          <Tag color="orange" style={{ fontSize: '10px' }}>Relationships</Tag>
                        </>
                      )}
                    </div>
                  )}
                </div>
                
                <div style={{ flex: 1, overflow: 'auto' }}>
                  <Tree
                    treeData={filteredTreeData}
                    expandedKeys={expandedKeys}
                    onExpand={(keys) => setExpandedKeys(keys as string[])}
                    showIcon
                    showLine={{ showLeafIcon: false }}
                    style={{ fontSize: '14px' }}
                  />
                </div>
              </div>
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <div>
                    <Text type="secondary">No schema data available</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      Select a project and data sources, then click "Extract Schemas"
                    </Text>
                  </div>
                }
              />
            )}
          </Card>
        </Col>
        </Row>
      )}
      
      {activeTab === 'test-vector' && (
        <Row gutter={[24, 24]}>
          {/* Vector Search Configuration Panel */}
          <Col xs={24} lg={8}>
            <Card title="Vector Search Configuration" style={{ height: '100%' }}>
              <Space direction="vertical" style={{ width: '100%' }} size="large">
                <div>
                  <Text strong style={{ display: 'block', marginBottom: '8px' }}>
                    Select Project
                  </Text>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="Choose a project"
                    loading={projectsLoading}
                    value={currentProject?.id}
                    onChange={() => {}}
                    showSearch
                    disabled
                    filterOption={(input, option) =>
                      option?.children?.toString().toLowerCase().includes(input.toLowerCase()) ?? false
                    }
                  >
                    {projects?.map((project: any) => (
                      <Option key={project.id} value={project.id}>
                        {project.name}
                      </Option>
                    ))}
                  </Select>
                </div>

                {currentProject?.id && (
                  <div>
                    <Text strong style={{ display: 'block', marginBottom: '8px' }}>
                      Data Source with Vectors
                    </Text>
                    <Select
                      style={{ width: '100%' }}
                      placeholder="Select data source"
                      value={selectedVectorDataSource}
                      onChange={setSelectedVectorDataSource}
                      loading={!availableDataSources.length}
                    >
                      {availableDataSources.map((ds: any) => (
                        <Option key={ds.id} value={ds.id}>
                          <Space>
                            <DatabaseOutlined />
                            {ds.name}
                            <Tag color={ds.hasVectors ? 'green' : 'orange'}>
                              {ds.connectorType.toUpperCase()}
                            </Tag>
                            {ds.hasVectors && <Tag color="blue">HAS VECTORS</Tag>}
                          </Space>
                        </Option>
                      ))}
                    </Select>
                    {availableDataSources.length === 0 && (
                      <Alert
                        type="info"
                        message="No data sources found"
                        description="Extract schemas first to populate vector data."
                        style={{ marginTop: '12px' }}
                        showIcon
                      />
                    )}
                  </div>
                )}

                <Divider />

                <div>
                  <Text strong style={{ display: 'block', marginBottom: '8px' }}>
                    Search Database Fields
                  </Text>
                  <Input.Search
                    placeholder={
                      !currentProject?.id 
                        ? "Please select a project first..." 
                        : !selectedVectorDataSource 
                        ? "Please select a data source first..."
                        : "e.g., user email, primary key, customer name, date fields..."
                    }
                    value={vectorSearchQuery}
                    onChange={(e) => {
                      console.log('Vector search input changed:', e.target.value);
                      setVectorSearchQuery(e.target.value);
                    }}
                    onSearch={searchFieldVectors}
                    loading={isSearchingVectors}
                    disabled={!currentProject?.id || !selectedVectorDataSource}
                    size="large"
                    enterButton={(
                      <Button 
                        type="primary" 
                        icon={<RocketOutlined />} 
                        style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                        disabled={!currentProject?.id || !selectedVectorDataSource}
                      >
                        Search
                      </Button>
                    )}
                  />
                  <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '8px' }}>
                    {!currentProject?.id || !selectedVectorDataSource 
                      ? 'Please select a project and data source above to enable search'
                      : 'Search for database fields by semantic meaning, data type, or field name'
                    }
                  </Text>
                </div>

                {/* Quick Search Examples */}
                <div>
                  <Text strong style={{ display: 'block', marginBottom: '8px' }}>
                    Quick Examples
                  </Text>
                  <Space wrap>
                    {[
                      'user email',
                      'primary key',
                      'customer name',
                      'date created',
                      'price amount',
                      'status flag',
                      'phone number',
                      'address field',
                      'unique identifier'
                    ].map(example => (
                      <Tag
                        key={example}
                        style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                        color="processing"
                        onClick={() => {
                          setVectorSearchQuery(example);
                          if (currentProject?.id && selectedVectorDataSource) {
                            setTimeout(() => searchFieldVectors(), 100);
                          }
                        }}
                      >
                        {example}
                      </Tag>
                    ))}
                  </Space>
                </div>

                {/* Vector Statistics */}
                {vectorStats && (
                  <div>
                    <Text strong style={{ display: 'block', marginBottom: '8px' }}>
                      Vector Statistics
                    </Text>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Statistic
                          title="Field Vectors"
                          value={vectorStats.schemaFieldVectors}
                          prefix={<DatabaseOutlined />}
                          valueStyle={{ fontSize: '18px' }}
                        />
                      </Col>
                      <Col span={12}>
                        <Statistic
                          title="Data Sources"
                          value={vectorStats.byDataSource?.length || 0}
                          prefix={<TableOutlined />}
                          valueStyle={{ fontSize: '18px' }}
                        />
                      </Col>
                    </Row>
                    <div style={{ marginTop: '12px' }}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        Data Types: {Object.keys(vectorStats.byDataType || {}).slice(0, 3).join(', ')}
                        {Object.keys(vectorStats.byDataType || {}).length > 3 && '...'}
                      </Text>
                    </div>
                  </div>
                )}

                {isLoadingStats && (
                  <div style={{ textAlign: 'center' }}>
                    <Spin size="small" />
                    <Text type="secondary" style={{ marginLeft: '8px', fontSize: '12px' }}>
                      Loading vector statistics...
                    </Text>
                  </div>
                )}
              </Space>
            </Card>
          </Col>

          {/* Vector Search Results Panel */}
          <Col xs={24} lg={16}>
            <Card
              title={
                <Space>
                  <Text strong>Field Vector Search Results</Text>
                  {vectorSearchResults.length > 0 && (
                    <Tag color="blue">{vectorSearchResults.length} results</Tag>
                  )}
                  {vectorSearchQuery && (
                    <Tag color="green">Query: "{vectorSearchQuery}"</Tag>
                  )}
                </Space>
              }
              extra={
                <Space>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={loadVectorStats}
                    loading={isLoadingStats}
                    size="small"
                  >
                    Refresh Stats
                  </Button>
                </Space>
              }
              style={{ height: '600px' }}
              styles={{ body: { height: 'calc(100% - 57px)', overflow: 'auto' } }}
            >
              {isSearchingVectors ? (
                <div style={{ textAlign: 'center', padding: '50px' }}>
                  <Spin size="large" />
                  <div style={{ marginTop: '16px' }}>
                    <Text>Searching field vectors...</Text>
                  </div>
                </div>
              ) : vectorSearchResults.length > 0 ? (
                <List
                  itemLayout="vertical"
                  dataSource={vectorSearchResults}
                  renderItem={(result: any, index: number) => (
                    <List.Item
                      key={`${result.field_id}-${index}`}
                      style={{
                        padding: '16px',
                        border: '1px solid #f0f0f0',
                        borderRadius: '8px',
                        marginBottom: '12px',
                        backgroundColor: index < 3 ? '#f6ffed' : '#fafafa'
                      }}
                    >
                      <List.Item.Meta
                        title={
                          <Space>
                            <Text strong style={{ color: '#1890ff', fontSize: '16px' }}>
                              {result.table_name}.{result.column_name}
                            </Text>
                            <Tag color="geekblue">{result.data_type}</Tag>
                            {result.is_primary_key && <Tag color="red">PRIMARY KEY</Tag>}
                            {!result.is_nullable && <Tag color="orange">NOT NULL</Tag>}
                            <Badge
                              count={`Score: ${(result.score * 100).toFixed(1)}%`}
                              style={{ backgroundColor: result.score > 0.8 ? '#52c41a' : result.score > 0.6 ? '#fa8c16' : '#1890ff' }}
                            />
                          </Space>
                        }
                        description={
                          <div>
                            <Text style={{ fontSize: '14px', marginBottom: '8px', display: 'block' }}>
                              {result.description}
                            </Text>
                            <div>
                              <Text type="secondary" style={{ fontSize: '12px' }}>Semantic Tags: </Text>
                              <Space wrap size="small">
                                {result.semantic_tags?.slice(0, 6).map((tag: string) => (
                                  <Tag key={tag} color="purple">{tag}</Tag>
                                ))}
                                {result.semantic_tags?.length > 6 && (
                                  <Tag color="default">+{result.semantic_tags.length - 6} more</Tag>
                                )}
                              </Space>
                            </div>
                          </div>
                        }
                      />
                    </List.Item>
                  )}
                />
              ) : (
                <Empty
                  image={<BugOutlined style={{ fontSize: '64px', color: '#d9d9d9' }} />}
                  description={
                    <div>
                      <Text type="secondary">
                        {!currentProject?.id || !selectedVectorDataSource
                          ? 'Select a project and data source to start testing'
                          : vectorSearchQuery
                          ? 'No matching fields found for your search'
                          : 'Enter a search query to find database fields'}
                      </Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        Try searching for terms like "email", "user id", "date", "amount", etc.
                      </Text>
                    </div>
                  }
                  style={{ padding: '50px' }}
                >
                  {currentProject?.id && selectedVectorDataSource && !vectorSearchQuery && (
                    <Button
                      type="primary"
                      icon={<RocketOutlined />}
                      onClick={() => {
                        setVectorSearchQuery('user email');
                        setTimeout(() => searchFieldVectors(), 100);
                      }}
                    >
                      Try Example Search
                    </Button>
                  )}
                </Empty>
              )}
            </Card>
          </Col>
        </Row>
      )}

      {activeTab === 'vectors' && (
        <Row gutter={[24, 24]}>
          <Col span={24}>
            <Card
              title={
                <Space>
                  <NodeIndexOutlined style={{ color: '#1890ff' }} />
                  Vector Database
                  {currentProject && (
                    <Tag color="blue">{currentProject.name}</Tag>
                  )}
                </Space>
              }
              extra={
                <Space>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={loadVectors}
                    loading={isLoadingVectors}
                  >
                    Refresh
                  </Button>
                </Space>
              }
            >
              {!currentProject && (
                <Alert
                  type="warning"
                  message="Please select a project from the header to view vectors"
                  style={{ marginBottom: '16px' }}
                />
              )}
              
              {currentProject && (
                <>
                  {/* Filter Controls */}
                  <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
                    <Col xs={24} sm={8}>
                      <Select
                        placeholder="Filter by connector"
                        style={{ width: '100%' }}
                        value={vectorFilter.connector}
                        onChange={(value) => setVectorFilter(prev => ({ ...prev, connector: value }))}
                        allowClear
                      >
                        <Option value="mssql">MSSQL</Option>
                        <Option value="postgresql">PostgreSQL</Option>
                        <Option value="mysql">MySQL</Option>
                        <Option value="oracle">Oracle</Option>
                        <Option value="sqlite">SQLite</Option>
                        <Option value="file">File</Option>
                        <Option value="url">URL</Option>
                      </Select>
                    </Col>
                    <Col xs={24} sm={8}>
                      <Select
                        placeholder="Filter by type"
                        style={{ width: '100%' }}
                        value={vectorFilter.type}
                        onChange={(value) => setVectorFilter(prev => ({ ...prev, type: value }))}
                        allowClear
                      >
                        <Option value="schema_field">Schema Field</Option>
                        <Option value="document">Document</Option>
                        <Option value="chunk">Chunk</Option>
                        <Option value="table">Table</Option>
                      </Select>
                    </Col>
                    <Col xs={24} sm={8}>
                      <Input
                        placeholder="Filter by source (partial match)"
                        value={sourceFilterInput}
                        onChange={(e) => setSourceFilterInput(e.target.value)}
                        allowClear
                        prefix={<SearchOutlined />}
                        onClear={() => {
                          setSourceFilterInput('');
                          setVectorFilter(prev => ({ ...prev, source: undefined }));
                        }}
                      />
                    </Col>
                  </Row>

                  {/* Vector List Table */}
                  <Table
                    dataSource={vectors}
                    loading={isLoadingVectors}
                    pagination={false}
                    rowKey="documentId"
                    columns={[
                      {
                        title: 'Document ID',
                        dataIndex: 'documentId',
                        key: 'documentId',
                        width: 200,
                        render: (text: string) => (
                          <Text code style={{ fontSize: '12px' }}>
                            {text.length > 20 ? `${text.substring(0, 20)}...` : text}
                          </Text>
                        ),
                      },
                      {
                        title: 'Type',
                        dataIndex: 'type',
                        key: 'type',
                        width: 120,
                        render: (type: string) => (
                          <Tag color={
                            type === 'schema_field' ? 'blue' :
                            type === 'document' ? 'green' :
                            type === 'chunk' ? 'orange' :
                            type === 'table' ? 'purple' : 'default'
                          }>
                            {type}
                          </Tag>
                        ),
                      },
                      {
                        title: 'Connector',
                        dataIndex: 'connector',
                        key: 'connector',
                        width: 120,
                        render: (connector: string) => (
                          <Tag color="cyan">
                            {connector?.toUpperCase()}
                          </Tag>
                        ),
                      },
                      {
                        title: 'Source',
                        dataIndex: 'source',
                        key: 'source',
                        width: 200,
                        render: (source: string) => (
                          <Tooltip title={source}>
                            <Text>
                              {source && source.length > 25 ? `${source.substring(0, 25)}...` : source}
                            </Text>
                          </Tooltip>
                        ),
                      },
                      {
                        title: 'Content Preview',
                        dataIndex: 'content',
                        key: 'content',
                        width: 300,
                        render: (content: any) => (
                          <Tooltip title={content?.text || 'No content'}>
                            <Text type="secondary">
                              {content?.text 
                                ? content.text.length > 50 
                                  ? `${content.text.substring(0, 50)}...` 
                                  : content.text
                                : 'No content'
                              }
                            </Text>
                          </Tooltip>
                        ),
                      },
                      {
                        title: 'Created',
                        dataIndex: 'createdAt',
                        key: 'createdAt',
                        width: 120,
                        render: (date: string) => (
                          <Text type="secondary">
                            {new Date(date).toLocaleDateString()}
                          </Text>
                        ),
                      },
                      {
                        title: 'Actions',
                        key: 'actions',
                        width: 120,
                        render: (_, record) => (
                          <Space size="small">
                            <Tooltip title="View Details">
                              <Button
                                type="text"
                                size="small"
                                icon={<EyeOutlined />}
                                onClick={() => showVectorDetail(record)}
                              />
                            </Tooltip>
                            <Tooltip title="Delete Vector">
                              <Popconfirm
                                title="Delete Vector"
                                description="Are you sure you want to delete this vector?"
                                onConfirm={() => deleteVector(record.documentId)}
                                okText="Yes"
                                cancelText="No"
                              >
                                <Button
                                  type="text"
                                  size="small"
                                  icon={<DeleteOutlined />}
                                  danger
                                />
                              </Popconfirm>
                            </Tooltip>
                          </Space>
                        ),
                      },
                    ]}
                  />

                  {/* Pagination */}
                  <div style={{ marginTop: '16px', textAlign: 'center' }}>
                    <Pagination
                      current={vectorPage}
                      total={vectorTotal}
                      pageSize={vectorPageSize}
                      showSizeChanger
                      showQuickJumper
                      showTotal={(total, range) => 
                        `${range[0]}-${range[1]} of ${total} vectors`
                      }
                      onChange={(page, size) => {
                        setVectorPage(page);
                        setVectorPageSize(size || 20);
                      }}
                      onShowSizeChange={(_, size) => {
                        setVectorPage(1);
                        setVectorPageSize(size);
                      }}
                    />
                  </div>

                  {/* Vector Detail Modal */}
                  <Modal
                    title="Vector Details"
                    open={isVectorDetailModalVisible}
                    onCancel={() => {
                      setIsVectorDetailModalVisible(false);
                      setIsLoadingVectorDetail(false);
                    }}
                    footer={[
                      <Button key="close" onClick={() => {
                        setIsVectorDetailModalVisible(false);
                        setIsLoadingVectorDetail(false);
                      }}>
                        Close
                      </Button>
                    ]}
                    width={800}
                  >
                    {isLoadingVectorDetail && (
                      <div style={{ textAlign: 'center', padding: '40px' }}>
                        <Spin size="large" />
                        <div style={{ marginTop: '16px' }}>Loading vector details...</div>
                      </div>
                    )}
                    {selectedVector && !isLoadingVectorDetail && (
                      <div>
                        <Row gutter={[16, 16]}>
                          <Col span={24}>
                            <Card size="small" title="Basic Information">
                              <Row gutter={[16, 8]}>
                                <Col xs={24} sm={12}>
                                  <Text strong>Document ID:</Text>
                                  <br />
                                  <Text code>{selectedVector.documentId}</Text>
                                </Col>
                                <Col xs={24} sm={12}>
                                  <Text strong>Type:</Text>
                                  <br />
                                  <Tag color="blue">{selectedVector.type}</Tag>
                                </Col>
                                <Col xs={24} sm={12}>
                                  <Text strong>Connector:</Text>
                                  <br />
                                  <Tag color="cyan">{selectedVector.connector}</Tag>
                                </Col>
                                <Col xs={24} sm={12}>
                                  <Text strong>Source:</Text>
                                  <br />
                                  <Text>{selectedVector.source}</Text>
                                </Col>
                              </Row>
                            </Card>
                          </Col>
                          <Col span={24}>
                            <Card size="small" title="Content">
                              <Text>{selectedVector.content?.text || 'No content available'}</Text>
                            </Card>
                          </Col>
                          {selectedVector.tags && Object.keys(selectedVector.tags).length > 0 && (
                            <Col span={24}>
                              <Card size="small" title="Tags">
                                <Space wrap>
                                  {Object.entries(selectedVector.tags).map(([key, value]) => (
                                    <Tag key={key} color="blue">
                                      {key}: {String(value)}
                                    </Tag>
                                  ))}
                                </Space>
                              </Card>
                            </Col>
                          )}
                          <Col span={24}>
                            <Card size="small" title="Vector Information">
                              <Row gutter={[16, 8]}>
                                <Col xs={24} sm={12}>
                                  <Text strong>Vector Dimensions:</Text>
                                  <br />
                                  <Text>{selectedVector.vectors?.length || 0}</Text>
                                </Col>
                                <Col xs={24} sm={12}>
                                  <Text strong>Created:</Text>
                                  <br />
                                  <Text>{new Date(selectedVector.createdAt).toLocaleString()}</Text>
                                </Col>
                              </Row>
                            </Card>
                          </Col>
                        </Row>
                      </div>
                    )}
                  </Modal>
                </>
              )}
            </Card>
          </Col>
        </Row>
      )}

      {activeTab === 'smart-query' && (
        <Row gutter={[24, 24]}>
          {/* Smart Query Input Panel */}
          <Col xs={24} lg={10}>
            <Card title="Smart Query" style={{ height: '100%' }}>
              <Space direction="vertical" style={{ width: '100%' }} size="large">
                {currentProject && (
                  <Alert
                    type="info"
                    message={`Working with project: ${currentProject.name}`}
                    style={{ marginBottom: '16px' }}
                  />
                )}
                
                {!currentProject && (
                  <Alert
                    type="warning"
                    message="Please select a project from the header to continue"
                    style={{ marginBottom: '16px' }}
                  />
                )}

                {currentProject && (
                  <div>
                    <Text strong style={{ display: 'block', marginBottom: '8px' }}>
                      Select Data Source
                    </Text>
                    <Select
                      style={{ width: '100%' }}
                      placeholder="Choose a data source"
                      value={selectedSmartQueryDataSource}
                      onChange={setSelectedSmartQueryDataSource}
                      loading={dataSourcesLoading}
                    >
                      {databaseDataSources.map((ds: DataSource) => (
                        <Option key={ds.id} value={ds.id}>
                          <Space>
                            <DatabaseOutlined />
                            {ds.name}
                            <Tag color="blue">
                              {ds.connectorType.toUpperCase()}
                            </Tag>
                          </Space>
                        </Option>
                      ))}
                    </Select>
                    {databaseDataSources.length === 0 && (
                      <Alert
                        type="info"
                        message="No database sources found"
                        description="Add database connectors (MSSQL, PostgreSQL, MySQL, etc.) to your project first."
                        style={{ marginTop: '12px' }}
                        showIcon
                      />
                    )}
                  </div>
                )}

                <Divider />

                <div>
                  <Text strong style={{ display: 'block', marginBottom: '8px' }}>
                    Natural Language Query
                  </Text>
                  <Input.TextArea
                    placeholder="e.g., what is the open workorder today this month, show me all users created last week..."
                    value={smartQuery}
                    onChange={(e) => setSmartQuery(e.target.value)}
                    disabled={!currentProject?.id || !selectedSmartQueryDataSource}
                    rows={4}
                    style={{ fontSize: '14px' }}
                  />
                  <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '8px' }}>
                    Enter your query in natural language. LangGraph will convert it to MSSQL.
                  </Text>
                </div>

                <Button
                  type="primary"
                  size="large"
                  icon={<RocketOutlined />}
                  onClick={processSmartQuery}
                  loading={isProcessingSmartQuery}
                  disabled={!currentProject?.id || !selectedSmartQueryDataSource || !smartQuery.trim()}
                  style={{ 
                    width: '100%',
                    backgroundColor: '#52c41a',
                    borderColor: '#52c41a',
                    fontWeight: 'bold'
                  }}
                >
                  {isProcessingSmartQuery ? 'Processing...' : 'Generate SQL Query'}
                </Button>

                {/* Quick Examples */}
                <div>
                  <Text strong style={{ display: 'block', marginBottom: '8px' }}>
                    Quick Examples
                  </Text>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    {[
                      'what is the open workorder today this month',
                      'show me all users created last week',
                      'find customers with pending orders',
                      'get total sales for this quarter',
                      'list employees by department',
                      'show recent transactions over $1000'
                    ].map(example => (
                      <Button
                        key={example}
                        type="dashed"
                        size="small"
                        style={{ textAlign: 'left', height: 'auto', padding: '8px 12px' }}
                        onClick={() => {
                          setSmartQuery(example);
                        }}
                        disabled={!currentProject?.id || !selectedSmartQueryDataSource}
                      >
                        {example}
                      </Button>
                    ))}
                  </Space>
                </div>
              </Space>
            </Card>
          </Col>

          {/* Smart Query Results Panel */}
          <Col xs={24} lg={14}>
            <Card
              title={
                <Space>
                  <Text strong>Query Results</Text>
                  {smartQueryResult && smartQueryResult.success && (
                    <Tag color="green">SUCCESS</Tag>
                  )}
                  {smartQueryResult && !smartQueryResult.success && (
                    <Tag color="red">FAILED</Tag>
                  )}
                </Space>
              }
              style={{ height: '600px' }}
              styles={{ body: { height: 'calc(100% - 57px)', overflow: 'auto' } }}
            >
              {isProcessingSmartQuery ? (
                <div style={{ textAlign: 'center', padding: '50px' }}>
                  <Spin size="large" />
                  <div style={{ marginTop: '16px' }}>
                    <Text>Processing your query with LangGraph...</Text>
                  </div>
                </div>
              ) : smartQueryResult ? (
                <div style={{ height: '100%', overflow: 'auto' }}>
                  {smartQueryResult.success ? (
                    <Space direction="vertical" style={{ width: '100%' }} size="large">
                      {/* Generated SQL */}
                      {smartQueryResult.generatedSQL && (
                        <Card size="small" title="Generated SQL Query" style={{ backgroundColor: '#f6ffed' }}>
                          <pre style={{ 
                            backgroundColor: '#f5f5f5', 
                            padding: '12px', 
                            borderRadius: '4px',
                            fontSize: '13px',
                            lineHeight: '1.5',
                            overflow: 'auto',
                            margin: 0
                          }}>
                            {smartQueryResult.generatedSQL}
                          </pre>
                        </Card>
                      )}

                      {/* Query Information */}
                      <Card size="small" title="Query Information">
                        <Row gutter={[16, 8]}>
                          <Col xs={24} sm={12}>
                            <Text strong>Original Query:</Text>
                            <br />
                            <Text>{smartQueryResult.query}</Text>
                          </Col>
                          <Col xs={24} sm={12}>
                            <Text strong>Confidence:</Text>
                            <br />
                            <Progress 
                              percent={Math.round(smartQueryResult.confidence * 100)} 
                              size="small"
                              status={smartQueryResult.confidence > 0.8 ? 'success' : smartQueryResult.confidence > 0.6 ? 'normal' : 'exception'}
                            />
                          </Col>
                          {smartQueryResult.classification && (
                            <Col xs={24} sm={12}>
                              <Text strong>Classification:</Text>
                              <br />
                              <Tag color="blue">{smartQueryResult.classification}</Tag>
                            </Col>
                          )}
                          {smartQueryResult.nodeUsed && (
                            <Col xs={24} sm={12}>
                              <Text strong>Node Used:</Text>
                              <br />
                              <Tag color="purple">{smartQueryResult.nodeUsed}</Tag>
                            </Col>
                          )}
                        </Row>
                      </Card>

                      {/* Data Source Information */}
                      {smartQueryResult.dataSourceInfo && (
                        <Card size="small" title="Data Source">
                          <Row gutter={[16, 8]}>
                            <Col xs={24} sm={12}>
                              <Text strong>Name:</Text>
                              <br />
                              <Text>{smartQueryResult.dataSourceInfo.name}</Text>
                            </Col>
                            <Col xs={24} sm={12}>
                              <Text strong>Type:</Text>
                              <br />
                              <Tag color="cyan">{smartQueryResult.dataSourceInfo.type?.toUpperCase()}</Tag>
                            </Col>
                            {smartQueryResult.dataSourceInfo.description && (
                              <Col span={24}>
                                <Text strong>Description:</Text>
                                <br />
                                <Text type="secondary">{smartQueryResult.dataSourceInfo.description}</Text>
                              </Col>
                            )}
                          </Row>
                        </Card>
                      )}

                      {/* Explanation */}
                      {smartQueryResult.explanation && (
                        <Card size="small" title="Explanation">
                          <Text style={{ whiteSpace: 'pre-wrap' }}>
                            {smartQueryResult.explanation}
                          </Text>
                        </Card>
                      )}

                      {/* Context Used */}
                      {smartQueryResult.context && smartQueryResult.context.length > 0 && (
                        <Card size="small" title={`Context Used (${smartQueryResult.context.length} items)`}>
                          <List
                            size="small"
                            dataSource={smartQueryResult.context.slice(0, 5)}
                            renderItem={(item: any, index: number) => (
                              <List.Item key={index}>
                                <List.Item.Meta
                                  title={<Text strong>{item.source || 'Unknown Source'}</Text>}
                                  description={
                                    <Text type="secondary" style={{ fontSize: '12px' }}>
                                      Score: {item.score?.toFixed(3)} | 
                                      {item.content?.text?.substring(0, 100)}...
                                    </Text>
                                  }
                                />
                              </List.Item>
                            )}
                          />
                          {smartQueryResult.context.length > 5 && (
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                              ... and {smartQueryResult.context.length - 5} more context items
                            </Text>
                          )}
                        </Card>
                      )}

                      {/* Metadata */}
                      {smartQueryResult.metadata && (
                        <Card size="small" title="Processing Metadata">
                          <Row gutter={[16, 8]}>
                            <Col xs={24} sm={12}>
                              <Text strong>Processing Time:</Text>
                              <br />
                              <Text>{smartQueryResult.metadata.processingTime}ms</Text>
                            </Col>
                            <Col xs={24} sm={12}>
                              <Text strong>Session ID:</Text>
                              <br />
                              <Text code style={{ fontSize: '11px' }}>
                                {smartQueryResult.metadata.sessionId?.substring(0, 8)}...
                              </Text>
                            </Col>
                          </Row>
                        </Card>
                      )}
                    </Space>
                  ) : (
                    <Alert
                      type="error"
                      message="Query Processing Failed"
                      description={smartQueryResult.error || 'An unknown error occurred'}
                      style={{ marginBottom: '16px' }}
                      showIcon
                    />
                  )}
                </div>
              ) : (
                <Empty
                  image={<FileTextOutlined style={{ fontSize: '64px', color: '#d9d9d9' }} />}
                  description={
                    <div>
                      <Text type="secondary">
                        {!currentProject?.id || !selectedSmartQueryDataSource
                          ? 'Select a project and data source to start'
                          : 'Enter a natural language query to generate SQL'}
                      </Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        Use natural language like "show me all users" or "find orders from last month"
                      </Text>
                    </div>
                  }
                  style={{ padding: '50px' }}
                >
                  {currentProject?.id && selectedSmartQueryDataSource && (
                    <Button
                      type="primary"
                      icon={<RocketOutlined />}
                      onClick={() => {
                        setSmartQuery('what is the open workorder today this month');
                      }}
                    >
                      Try Example Query
                    </Button>
                  )}
                </Empty>
              )}
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default QueryGeneratorPage;